// useHoldToConfirm — pointer-driven hold-to-confirm primitive.
//
// Tap-and-release: nothing. Press-and-hold for `durationMs`: fires
// `onConfirm`. Move pointer too far (>8px), release early, or pointer
// cancel → fires `onCancel` and resets.
//
// Returns:
//   - bind: spread onto the target element to wire pointer events
//   - progress (0–1): how far through the hold we are; use to drive a
//     visual fill (ring, bar, etc.)
//   - holding: boolean, true while the user is actively pressing
//
// Honors prefers-reduced-motion only insofar as the caller decides;
// the hook itself is timing-agnostic.

import { useCallback, useEffect, useRef, useState } from 'react';

interface HoldToConfirmOptions {
  durationMs?: number;
  cancelMoveThresholdPx?: number;
  onConfirm: () => void;
  onCancel?: () => void;
}

interface BindHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerLeave: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface HoldToConfirmState {
  bind: BindHandlers;
  progress: number;
  holding: boolean;
}

export function useHoldToConfirm({
  durationMs = 600,
  cancelMoveThresholdPx = 8,
  onConfirm,
  onCancel,
}: HoldToConfirmOptions): HoldToConfirmState {
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const startTsRef = useRef<number | null>(null);
  const startXYRef = useRef<{ x: number; y: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startTsRef.current = null;
    startXYRef.current = null;
    setHolding(false);
    setProgress(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const tick = useCallback(() => {
    if (startTsRef.current == null) return;
    const elapsed = Date.now() - startTsRef.current;
    const p = Math.min(1, elapsed / durationMs);
    setProgress(p);
    if (p >= 1 && !firedRef.current) {
      firedRef.current = true;
      onConfirm();
      cleanup();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [durationMs, onConfirm, cleanup]);

  const bind: BindHandlers = {
    onPointerDown: (e) => {
      e.preventDefault();
      firedRef.current = false;
      startTsRef.current = Date.now();
      startXYRef.current = { x: e.clientX, y: e.clientY };
      setHolding(true);
      try {
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        // setPointerCapture can throw on certain elements; non-blocking.
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    onPointerMove: (e) => {
      if (!startXYRef.current) return;
      const dx = e.clientX - startXYRef.current.x;
      const dy = e.clientY - startXYRef.current.y;
      if (Math.hypot(dx, dy) > cancelMoveThresholdPx) {
        if (!firedRef.current) onCancel?.();
        cleanup();
      }
    },
    onPointerUp: () => {
      if (!firedRef.current && startTsRef.current != null) onCancel?.();
      cleanup();
    },
    onPointerLeave: () => {
      if (!firedRef.current && startTsRef.current != null) onCancel?.();
      cleanup();
    },
    onPointerCancel: () => {
      if (!firedRef.current && startTsRef.current != null) onCancel?.();
      cleanup();
    },
  };

  return { bind, progress, holding };
}
